// ============================================================
// ActivityTypePicker.tsx — the LIGHT "what are you logging?" choice.
// Approved mockup: outputs/casevo-activity-picker-and-view.html
//   • Option A (default) — <LogTimeButton>: the "Log time" button opens a
//     small POPOVER anchored under it holding the type tiles (Make call /
//     Take call / Letter / Memo in their timeline colours + the dashed
//     Send-email hand-off). Replaces the old split button (Log time +
//     chevron menu) AND the heavy full-modal picker step it used to open.
//   • Option B (fallback) — <ActivityTypePickerCard>: the same tiles in a
//     compact card on the bare ModalFrame (the light confirm's weight), for
//     any opener with no button to anchor a popover to. AddActivityModal
//     renders it for its picker step (variant-less open).
// Nothing is recorded here: picking a type opens the add-activity form,
// whose timer starts at that moment (AddActivityModal's variant branch).
// NOTE: deliberately imports only the TYPE ActionVariantKey from
// AddActivityModal (which imports this file) — no runtime cycle.
// ============================================================
import React, { useEffect, useId, useRef, useState } from 'react'
import type { ActionVariantKey } from './AddActivityModal'
import ModalFrame from './ModalFrame'
import { TONES, FUTURE_ENTRY_TYPES, entryTypeFor, resolveEntryTone } from '../../constants/entryTypes'
import { useCasevoStore } from '../../store/useCasevoStore'
import { EntryIconSvg, UiIconSvg } from '../clients/activityIcons'
import { DIALOG_BTN_CANCEL } from './ConfirmDialogue'

// The four timed types, in picker order: variant key → stored action value
// (ACTION_TYPES vocabulary) + the one-line description shown on the tile.
const TYPES: { key: ActionVariantKey; value: string; label: string; sub: string }[] = [
  { key: 'call', value: 'call', label: 'Make call', sub: 'Outgoing call' },
  { key: 'takeCall', value: 'take_call', label: 'Take call', sub: 'Incoming call' },
  { key: 'letter', value: 'letter', label: 'Letter', sub: 'Sent or received' },
  { key: 'memo', value: 'memo', label: 'Memo', sub: 'Note to file' },
]

// Timeline tone for a type — honours the firm/user Timeline Colours schemes,
// exactly like the feed's tiles (ClientActivityFeed `tone`).
const useTone = (): ((key: string) => string) => {
  const firm = useCasevoStore((s) => s.firmColourScheme)
  const user = useCasevoStore((s) => s.userColourScheme)
  return (key: string) => TONES[resolveEntryTone(key, firm, user)]
}

const TILE =
  'flex items-center gap-[10px] w-full text-left rounded-[12px] border border-casevo-border bg-casevo-surface px-[10px] py-[10px] ' +
  'hover:border-casevo-mid hover:bg-[color-mix(in_srgb,var(--casevo-mint)_40%,var(--casevo-surface))] transition-colors focus:outline-none casevo-focus'

interface TilesProps {
  onPick: (v: ActionVariantKey) => void
  onEmail?: () => void
  /** Show the "Coming soon" roadmap line (the old Log-time menu carried it). */
  showSoon?: boolean
  /** The ONE "Invoice" action — raises a bill (RaiseBillModal). */
  onCreateInvoice?: () => void
  /** No single case in scope (the client-wide tab) → it asks for the case first. */
  invoiceNeedsCase?: boolean
  autoFocus?: boolean
}

// The shared tile grid (2×2) + the dashed Send-email hand-off row. Arrow
// keys move between tiles; Enter/Space picks (they're real buttons).
export const ActivityTypeTiles: React.FC<TilesProps> = ({ onPick, onEmail, showSoon = false, autoFocus = false, onCreateInvoice, invoiceNeedsCase = false }) => {
  const tone = useTone()
  const wrapRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!autoFocus) return
    const id = requestAnimationFrame(() => wrapRef.current?.querySelector<HTMLButtonElement>('button')?.focus())
    return () => cancelAnimationFrame(id)
  }, [autoFocus])
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return
    const btns = Array.from(wrapRef.current?.querySelectorAll<HTMLButtonElement>('button[data-tile]') ?? [])
    const i = btns.indexOf(document.activeElement as HTMLButtonElement)
    if (i === -1) return
    e.preventDefault()
    // Grid of 2 columns (then the full-width email row).
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? 2 : -2
    const next = Math.min(btns.length - 1, Math.max(0, i + step))
    btns[next]?.focus()
  }
  return (
    <div ref={wrapRef} onKeyDown={onKeyDown}>
      <div className="grid grid-cols-2 gap-2">
        {TYPES.map((t) => (
          <button key={t.key} type="button" data-tile="" className={TILE} onClick={() => onPick(t.key)}>
            <span className={`w-8 h-8 rounded-[9px] grid place-items-center shrink-0 ${tone(t.value)}`} aria-hidden="true">
              <EntryIconSvg icon={entryTypeFor(t.value).icon} size={16} />
            </span>
            <span className="min-w-0">
              <b className="block text-[13.5px] font-semibold text-casevo-forest leading-tight">{t.label}</b>
              <small className="block text-[11.5px] text-casevo-text-soft truncate">{t.sub}</small>
            </span>
          </button>
        ))}
      </div>
      {onEmail && (
        <button type="button" data-tile="" className={`${TILE} !border-dashed mt-2`} onClick={onEmail}>
          <span className={`w-8 h-8 rounded-[9px] grid place-items-center shrink-0 ${tone('email')}`} aria-hidden="true">
            <EntryIconSvg icon="mail" size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <b className="block text-[13.5px] font-semibold text-casevo-forest leading-tight">Send email</b>
            <small className="block text-[11.5px] text-casevo-text-soft">Opens the composer</small>
          </span>
          <UiIconSvg icon="chev" size={14} className="-rotate-90 text-casevo-text-soft" />
        </button>
      )}
      {onCreateInvoice && (
        <button type="button" data-tile="" className={`${TILE} !border-dashed mt-2`} onClick={onCreateInvoice}>
          <span className={`w-8 h-8 rounded-[9px] grid place-items-center shrink-0 ${tone('invoice')}`} aria-hidden="true">
            <EntryIconSvg icon="invoice" size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <b className="block text-[13.5px] font-semibold text-casevo-forest leading-tight">{entryTypeFor('invoice').label}</b>
            <small className="block text-[11.5px] text-casevo-text-soft">{invoiceNeedsCase ? 'Choose a case, then raise a bill' : 'Raise a bill for this case'}</small>
          </span>
          <UiIconSvg icon="chev" size={14} className="-rotate-90 text-casevo-text-soft" />
        </button>
      )}
      {showSoon && FUTURE_ENTRY_TYPES.some((t) => t.soonAction) && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-[10px] px-1 text-[11.5px] text-casevo-text-soft">
          <span className="font-bold tracking-[0.12em] uppercase text-[10.5px]">Soon</span>
          {FUTURE_ENTRY_TYPES.filter((t) => t.soonAction).map((t) => (
            <span key={t.key} className="inline-flex items-center gap-1 opacity-80" title="Coming soon">
              <EntryIconSvg icon={t.icon} size={12} />{t.soonAction}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Option A: the "Log time" button + its anchored popover ─────────────
interface LogTimeButtonProps {
  onPick: (v: ActionVariantKey) => void
  onEmail?: () => void
  /** Button classes (callers keep their own sizing — header vs empty state). */
  className: string
  /** Popover alignment under the button. */
  align?: 'right' | 'center'
  /** The ONE "Invoice" action (raises a bill) — see ActivityTypeTiles. */
  onCreateInvoice?: () => void
  invoiceNeedsCase?: boolean
}
export const LogTimeButton: React.FC<LogTimeButtonProps> = ({ onPick, onEmail, className, align = 'right', onCreateInvoice, invoiceNeedsCase }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const popId = useId()
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
      btnRef.current?.focus()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])
  const pick = (v: ActionVariantKey): void => { setOpen(false); onPick(v) }
  const email = onEmail ? (): void => { setOpen(false); onEmail() } : undefined
  const invoice = onCreateInvoice ? (): void => { setOpen(false); onCreateInvoice() } : undefined
  return (
    <div className="relative inline-flex" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={className}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <UiIconSvg icon="clock" />Log time<UiIconSvg icon="chev" size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          id={popId}
          role="dialog"
          aria-label="Log activity"
          className={`absolute top-[calc(100%+8px)] z-30 w-[372px] max-w-[calc(100vw-32px)] text-left bg-casevo-surface border border-casevo-border rounded-[14px] p-3 shadow-[0_18px_44px_rgba(10,25,17,0.20),0_2px_6px_rgba(10,25,17,0.08)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.3)] ${align === 'center' ? 'left-1/2 -translate-x-1/2' : 'right-0'}`}
        >
          <div className="flex items-baseline justify-between gap-3 px-1 pt-0.5 pb-[10px]">
            <b className="text-[14px] font-semibold text-casevo-forest">Log activity</b>
            <span className="text-[11.5px] text-casevo-text-soft">Timer starts when you pick</span>
          </div>
          <ActivityTypeTiles onPick={pick} onEmail={email} onCreateInvoice={invoice} invoiceNeedsCase={invoiceNeedsCase} showSoon autoFocus />
        </div>
      )}
    </div>
  )
}

// ── Option B: the compact card (bare ModalFrame, like ConfirmDialogue) ──
interface CardProps {
  isOpen: boolean
  onClose: () => void
  onPick: (v: ActionVariantKey) => void
  onEmail?: () => void
  /** Context line under the title, e.g. the client's name. */
  subtitle?: string
}
export const ActivityTypePickerCard: React.FC<CardProps> = ({ isOpen, onClose, onPick, onEmail, subtitle }) => (
  <ModalFrame
    isOpen={isOpen}
    onClose={onClose}
    bare
    title="Log activity"
    ariaLabel="Log activity"
    className="max-w-[440px] rounded-[14px] border border-casevo-border bg-casevo-surface text-casevo-forest pt-[18px] px-[18px] pb-[14px] shadow-[0_18px_44px_rgba(10,25,17,0.20),0_2px_6px_rgba(10,25,17,0.08)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.3)]"
  >
    <div className="flex items-center gap-3 mb-[14px]">
      <span aria-hidden="true" className="w-[38px] h-[38px] rounded-full grid place-items-center shrink-0 bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]">
        <UiIconSvg icon="clock" size={18} />
      </span>
      <div className="min-w-0">
        <h3 className="m-0 text-[16px] font-semibold text-casevo-forest leading-tight">Log activity</h3>
        {subtitle && <p className="m-0 text-[12.5px] text-casevo-text-soft truncate">{subtitle}</p>}
      </div>
      <button type="button" onClick={onClose} aria-label="Close" className="ml-auto self-start w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-input focus:outline-none casevo-focus">
        <UiIconSvg icon="x" size={15} />
      </button>
    </div>
    <ActivityTypeTiles onPick={onPick} onEmail={onEmail} autoFocus />
    <div className="flex items-center gap-2 mt-[14px]">
      <span className="inline-flex items-center gap-1.5 text-[12.5px] text-casevo-text-soft"><UiIconSvg icon="clock" size={13} />The timer starts when you pick</span>
      <button type="button" onClick={onClose} className={`ml-auto ${DIALOG_BTN_CANCEL}`}>Cancel</button>
    </div>
  </ModalFrame>
)
